import React from "react";
import ModalShell from "./ModalShell";
import Field from "./Field";
import { reportBreaks } from "../api/reports";
import Button from "./Shared/Button";
import ModalStep from "./Shared/ModalStep";

/** Det klassidan behöver skicka med för att rapporteringen ska gå att göra. */
export interface ReportBreakConfig {
  /** Nyckeln ur klasslänkens #key=. */
  token: string;
  /** Taket för ett enskilt Breaks längd från BreaksOptions. */
  maxMinutesPerBreak: number;
  /** Taket för antal deltagande elever från BreaksOptions. */
  maxStudentsPerClass: number;
  /** Antalet elever läraren angav vid anmälan. Används som förifyllt värde. */
  registeredStudents: number;
  className: string;
}

interface ReportBreakModalProps {
  config: ReportBreakConfig;
  onClose: () => void;
  /** Anropas när en rapport har sparats, så att sidans siffror kan läsas om. */
  onReported: () => void;
}

/** De vanligaste längderna. Allt annat skrivs in via "Annat". */
const PRESET_MINUTES = [5, 10, 15] as const;

const CUSTOM = "custom";

/** Datumfältet vill ha "yyyy-mm-dd" i lokal tid, inte UTC som toISOString ger. */
function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export default function ReportBreakModal({
  config,
  onClose,
  onReported,
}: ReportBreakModalProps): React.JSX.Element {
  const today = React.useMemo(() => toIsoDate(new Date()), []);

  const [date, setDate] = React.useState(today);
  const [duration, setDuration] = React.useState<number | typeof CUSTOM>(
    PRESET_MINUTES[0],
  );
  const [customMinutes, setCustomMinutes] = React.useState("");
  // Oftast är hela klassen med, så det registrerade antalet är en rimlig utgångspunkt.
  const [students, setStudents] = React.useState(
    String(config.registeredStudents),
  );
  const [touched, setTouched] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [total, setTotal] = React.useState<number | null>(null);
  const [done, setDone] = React.useState(false);

  const minutesPerBreak =
    duration === CUSTOM ? Number.parseInt(customMinutes, 10) : duration;
  const participatingStudents = Number.parseInt(students, 10);

  const errors = {
    // Framtida datum säger domänen ändå nej till, men felet blir tydligare här.
    date: date === "" || date > today,
    minutes:
      Number.isNaN(minutesPerBreak) ||
      minutesPerBreak < 1 ||
      minutesPerBreak > config.maxMinutesPerBreak,
    students:
      Number.isNaN(participatingStudents) ||
      participatingStudents < 0 ||
      participatingStudents > config.maxStudentsPerClass,
  };
  const valid = !errors.date && !errors.minutes && !errors.students;

  const pickDuration = (value: number | typeof CUSTOM) => (): void => {
    setDuration(value);
  };

  const submit = async (): Promise<void> => {
    // Dubbelklick på knappen skulle annars skicka två rapporter.
    if (submitting) return;

    if (!valid) {
      setTouched(true);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const result = await reportBreaks({
        token: config.token,
        date,
        minutesPerBreak,
        participatingStudents,
      });

      setTotal(result.totalForClass);
      setDone(true);
      onReported();
    } catch (cause: unknown) {
      setError(
        cause instanceof Error ? cause.message : "Rapporten kunde inte sparas.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell onClose={onClose} dialogClassName="report-modal__dialog">
      <div className="modal-content report-modal__content">
        <div className="report-modal__head">
          <p className="breaks-eyebrow mb-0">{done ? "Klart" : "Rapportera"}</p>
          <button
            type="button"
            className="report-modal__close"
            onClick={onClose}
            aria-label="Stäng rapportering"
          >
            &times;
          </button>
        </div>

        {done ? (
          <>
            <span className="breaks-graphic__pause" aria-hidden="true">
              <span />
              <span />
            </span>
            <ModalStep title="Tack! Ert Break är registrerat.">
              <p className="mb-0">
                {total === null
                  ? `Rapporten för ${date} är sparad.`
                  : `${config.className || "Klassen"} har nu gjort ${total} Breaks totalt.`}
              </p>
              <div className="report-modal__actions">
                <Button variant="primary" onClick={onClose}>
                  Stäng
                </Button>
              </div>
            </ModalStep>
          </>
        ) : (
          <ModalStep title="Rapportera ett Break">
            <p className="breaks-field__hint mb-0">
              Varje rapport är ett Break. Gjorde ni flera samma dag rapporterar
              ni ett i taget.
            </p>

            <Field
              id="report-date"
              label="Datum"
              invalid={touched && errors.date}
              hint={
                touched && errors.date
                  ? "Välj ett datum som inte ligger i framtiden."
                  : undefined
              }
            >
              <input
                id="report-date"
                type="date"
                className="breaks-field__input"
                value={date}
                max={today}
                onChange={(event) => setDate(event.target.value)}
                aria-required="true"
                aria-invalid={touched && errors.date}
                aria-describedby={
                  touched && errors.date ? "report-date-hint" : undefined
                }
              />
            </Field>

            <div className="breaks-field">
              <span className="breaks-field__label" id="report-duration-label">
                Hur länge höll ni på?
              </span>
              <div
                className="duration-pills"
                role="radiogroup"
                aria-labelledby="report-duration-label"
              >
                {PRESET_MINUTES.map((minutes) => (
                  <label className="duration-pills__pill" key={minutes}>
                    <input
                      type="radio"
                      name="report-duration"
                      className="duration-pills__input"
                      checked={duration === minutes}
                      onChange={pickDuration(minutes)}
                    />
                    <span className="duration-pills__label">{minutes} min</span>
                  </label>
                ))}
                <label className="duration-pills__pill">
                  <input
                    type="radio"
                    name="report-duration"
                    className="duration-pills__input"
                    checked={duration === CUSTOM}
                    onChange={pickDuration(CUSTOM)}
                  />
                  <span className="duration-pills__label">Annat</span>
                </label>
              </div>
            </div>

            {duration === CUSTOM && (
              <Field
                id="report-minutes"
                label="Antal minuter per Break"
                invalid={touched && errors.minutes}
                hint={
                  touched && errors.minutes
                    ? `Ange ett tal mellan 1 och ${config.maxMinutesPerBreak}.`
                    : undefined
                }
              >
                <input
                  id="report-minutes"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={config.maxMinutesPerBreak}
                  className="breaks-field__input breaks-field__input--narrow"
                  value={customMinutes}
                  onChange={(event) => setCustomMinutes(event.target.value)}
                  aria-required="true"
                  aria-invalid={touched && errors.minutes}
                  aria-describedby={
                    touched && errors.minutes
                      ? "report-minutes-hint"
                      : undefined
                  }
                />
              </Field>
            )}

            <Field
              id="report-students"
              label="Hur många elever var med?"
              invalid={touched && errors.students}
              hint={
                touched && errors.students
                  ? `Ange ett tal mellan 0 och ${config.maxStudentsPerClass}.`
                  : undefined
              }
            >
              <div className="d-flex align-items-center gap-2">
                <input
                  id="report-students"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={config.maxStudentsPerClass}
                  className="breaks-field__input breaks-field__input--narrow"
                  value={students}
                  onChange={(event) => setStudents(event.target.value)}
                  aria-required="true"
                  aria-invalid={touched && errors.students}
                  aria-describedby={
                    touched && errors.students
                      ? "report-students-hint report-students-registered"
                      : "report-students-registered"
                  }
                />
                <span
                  className="breaks-field__hint mb-0"
                  id="report-students-registered"
                >
                  (registrerat antal: {config.registeredStudents})
                </span>
              </div>
            </Field>

            {error && (
              <p
                className="breaks-field__hint breaks-field__hint--error mb-0"
                role="alert"
              >
                {error}
              </p>
            )}

            <div className="report-modal__actions">
              <Button variant="link" onClick={onClose} disabled={submitting}>
                Avbryt
              </Button>
              <Button
                variant="primary"
                onClick={submit}
                disabled={submitting}
                aria-busy={submitting}
              >
                {submitting ? "Rapporterar…" : "Rapportera"}
              </Button>
            </div>
          </ModalStep>
        )}
      </div>
    </ModalShell>
  );
}
